import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import useMarkBreweryReviewsSeen from '../hooks/useMarkBreweryReviewsSeen';
import useBreweryReviewSeenSet from '../hooks/useBreweryReviewSeenSet';
import { buildReviewSeenKey } from '../utils/reviewDetails/buildReviewSeenDocId';
import {
  REVIEW_SEEN_FLUSH_BATCH_SIZE,
  REVIEW_SEEN_FLUSH_INTERVAL_MS,
} from '../utils/reviewDetails/reviewDetailsConstants';
import { trackReviewDetailsMarkReviewsSeen } from '../utils/brewhousePortalAnalytics';

const ReviewDetailsSeenContext = createContext(null);

/**
 * @param {string | null | undefined} breweryId
 * @param {'beer' | 'hospitality' | 'event'} reviewType
 * @param {string} reviewId
 */
function buildQueueItem(breweryId, reviewType, reviewId) {
  return { breweryId, reviewType, reviewId };
}

export function ReviewDetailsSeenProvider({ breweryId, activeTab, children }) {
  const { seenKeys: remoteSeenKeys, loading: seenLoading } = useBreweryReviewSeenSet(breweryId);
  const { mutate } = useMarkBreweryReviewsSeen();
  const [optimisticSeenKeys, setOptimisticSeenKeys] = useState(() => new Set());
  const queueRef = useRef([]);
  const queuedKeysRef = useRef(new Set());
  const flushInFlightRef = useRef(false);
  const seenKeysRef = useRef(new Set());

  const seenKeys = useMemo(() => {
    const merged = new Set(remoteSeenKeys);
    optimisticSeenKeys.forEach((seenKey) => merged.add(seenKey));
    return merged;
  }, [optimisticSeenKeys, remoteSeenKeys]);

  useEffect(() => {
    seenKeysRef.current = seenKeys;
  }, [seenKeys]);

  const isUnseen = useCallback((reviewType, reviewId) => {
    return !seenKeysRef.current.has(buildReviewSeenKey(reviewType, reviewId));
  }, []);

  const flushQueue = useCallback(async () => {
    if (flushInFlightRef.current || queueRef.current.length === 0) {
      return;
    }

    const batch = queueRef.current.splice(0, REVIEW_SEEN_FLUSH_BATCH_SIZE);
    if (batch.length === 0) {
      return;
    }

    const itemsByBrewery = new Map();
    for (const item of batch) {
      const itemBreweryId = typeof item.breweryId === 'string' ? item.breweryId.trim() : '';
      if (!itemBreweryId) {
        continue;
      }
      const breweryItems = itemsByBrewery.get(itemBreweryId) ?? [];
      breweryItems.push(item);
      itemsByBrewery.set(itemBreweryId, breweryItems);
    }

    if (itemsByBrewery.size === 0) {
      queueRef.current.unshift(...batch);
      return;
    }

    flushInFlightRef.current = true;
    try {
      const allMarkedItems = [];

      for (const [batchBreweryId, breweryItems] of itemsByBrewery) {
        const result = await mutate({
          breweryId: batchBreweryId,
          items: breweryItems.map((item) => ({
            reviewType: item.reviewType,
            reviewId: item.reviewId,
          })),
        });
        const markedItems = Array.isArray(result?.marked) ? result.marked : breweryItems;
        allMarkedItems.push(...markedItems);

        const markedKeySet = new Set(
          markedItems.map((item) => buildReviewSeenKey(item.reviewType, item.reviewId)),
        );

        const unconfirmedItems = [];
        breweryItems.forEach((item) => {
          const seenKey = buildReviewSeenKey(item.reviewType, item.reviewId);
          queuedKeysRef.current.delete(seenKey);
          if (!markedKeySet.has(seenKey)) {
            unconfirmedItems.push(item);
          }
        });
        if (unconfirmedItems.length > 0) {
          queueRef.current.unshift(...unconfirmedItems);
        }

        trackReviewDetailsMarkReviewsSeen({
          breweryId: batchBreweryId,
          markedCount: markedItems.length,
          tab: activeTab,
        });
      }

      setOptimisticSeenKeys((currentKeys) => {
        const nextKeys = new Set(currentKeys);
        allMarkedItems.forEach((item) => {
          nextKeys.add(buildReviewSeenKey(item.reviewType, item.reviewId));
        });
        return nextKeys;
      });
    } catch {
      batch.forEach((item) => {
        queuedKeysRef.current.delete(buildReviewSeenKey(item.reviewType, item.reviewId));
      });
      queueRef.current.unshift(...batch);
    } finally {
      flushInFlightRef.current = false;
    }
  }, [activeTab, mutate]);

  const enqueueSeen = useCallback((reviewType, reviewId) => {
    if (typeof breweryId !== 'string' || breweryId.length === 0) {
      return;
    }
    const seenKey = buildReviewSeenKey(reviewType, reviewId);
    if (seenKeysRef.current.has(seenKey) || queuedKeysRef.current.has(seenKey)) {
      return;
    }
    queuedKeysRef.current.add(seenKey);
    queueRef.current.push(buildQueueItem(breweryId, reviewType, reviewId));
    if (queueRef.current.length >= REVIEW_SEEN_FLUSH_BATCH_SIZE) {
      flushQueue();
    }
  }, [breweryId, flushQueue]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (queueRef.current.length > 0) {
        flushQueue();
      }
    }, REVIEW_SEEN_FLUSH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [flushQueue]);

  const value = useMemo(() => ({
    seenKeys,
    seenLoading,
    isUnseen,
    enqueueSeen,
    flushQueue,
  }), [enqueueSeen, flushQueue, isUnseen, seenKeys, seenLoading]);

  return (
    <ReviewDetailsSeenContext.Provider value={value}>
      {children}
    </ReviewDetailsSeenContext.Provider>
  );
}

ReviewDetailsSeenProvider.propTypes = {
  breweryId: PropTypes.string,
  activeTab: PropTypes.oneOf(['new-reviews', 'all-reviews']).isRequired,
  children: PropTypes.node.isRequired,
};

export function useReviewDetailsSeen() {
  const context = useContext(ReviewDetailsSeenContext);
  if (!context) {
    throw new Error('useReviewDetailsSeen must be used within ReviewDetailsSeenProvider');
  }
  return context;
}

export default ReviewDetailsSeenContext;
